/**
 * Datos de la clínica que usa esta instalación (2026-07-27).
 *
 * Las pantallas llevaban el nombre de la clínica, su dirección y su correo
 * escritos a mano, así que cualquier copia del producto los enseñaba. Ahora
 * salen de `/api/public/branding`, que los lee de la configuración.
 *
 * Sin login a propósito: lo usan el formulario de LOPD, el consentimiento de
 * la tablet y la pantalla de entrada, donde todavía no hay sesión.
 */
import { useEffect, useState } from 'react';

const VACIO = {
  clinic_name: '',
  legal_entity: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  doctor_name: '',
  // Solo cambian en el servidor de demostraciones (varias especialidades en
  // un mismo servidor). En una instalación real quedan siempre así.
  clinic_profile: 'plastica',
  demo_autologin: false,
  demo_password: '',
  // Los botones «Entrar como…» del login en la demo: [{label, email}].
  demo_accounts: [],
};

// Una sola petición por carga de página, compartida por todo el que la pida.
// Solo se guarda una respuesta BUENA (23/9/2026): un 502 se guardaba como si
// la clínica no tuviera teléfono hasta recargar la página, y «Reintentar» ya
// no lo recuperaba aunque el servidor hubiera vuelto. Un fallo se vuelve a
// pedir la próxima vez que alguien lo pida.
let cache = null;
let enVuelo = null;

export function cargarClinica() {
  if (cache) return Promise.resolve(cache);
  if (!enVuelo) {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    enVuelo = fetch(`${base}/api/public/branding`)
      .then((r) => {
        if (!r.ok) throw new Error(`branding ${r.status}`);
        return r.json();
      })
      .then((d) => { cache = { ...VACIO, ...d }; return cache; })
      // Si falla, se pinta sin los datos antes que romper la pantalla: el
      // formulario del paciente tiene que salir igual. Y SIN guardarlo.
      .catch(() => VACIO)
      .finally(() => { enVuelo = null; });
  }
  return enVuelo;
}

/** Olvida lo leído: la próxima `cargarClinica` vuelve a preguntar. Para las
 *  pruebas, que necesitan empezar cada una sin la Configuración de la
 *  anterior (en la app, lo leído bien vale para toda la carga de la página). */
export function olvidarClinica() {
  cache = null;
  enVuelo = null;
}

/** Los datos de la clínica en un componente. Empieza vacío y se rellena. */
export function useClinica() {
  const [datos, setDatos] = useState(cache || VACIO);
  useEffect(() => {
    let vivo = true;
    cargarClinica().then((d) => { if (vivo) setDatos(d); });
    return () => { vivo = false; };
  }, []);
  return datos;
}

/** Una línea con lo que se pone al pie: nombre · razón social · dirección · teléfono. */
export function pieClinica(c) {
  return [c.clinic_name, c.legal_entity, c.address, c.phone]
    .filter(Boolean)
    .join(' · ');
}
